import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Home.css';

function Home() {
  const { user, selectedGroup, setSelectedGroup } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('group');
  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupMembers(selectedGroup._id);
    }
  }, [selectedGroup]);

  const loadData = async () => {
    try {
      const [groupsData, usersData] = await Promise.all([
        api.getMyGroups(),
        api.getAllUsers()
      ]);

      setGroups(groupsData);
      setAllUsers(usersData);

      if (groupsData.length > 0 && !selectedGroup) {
        setSelectedGroup(groupsData[0]);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMembers = async (groupId) => {
    try {
      const members = await api.getGroupMembers(groupId);
      setGroupMembers(members);
    } catch (err) {
      console.error('Failed to load group members:', err);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const newGroup = await api.createGroup(newGroupName);
      setGroups([...groups, newGroup]);
      setSelectedGroup(newGroup);
      setShowCreateGroup(false);
      setNewGroupName('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const joinedGroup = await api.joinGroup(inviteCode);
      setGroups([...groups, joinedGroup]);
      setSelectedGroup(joinedGroup);
      setShowJoinGroup(false);
      setInviteCode('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUserClick = (userId) => {
    if (selectedGroup) {
      navigate(`/user/${userId}/group/${selectedGroup._id}`);
    }
  };

  const handleDeleteClick = (e, member) => {
    e.stopPropagation();
    setUserToDelete(member);
    setAdminPassword('');
    setError('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await api.deleteUser(userToDelete._id, adminPassword);
      setAllUsers(allUsers.filter(u => u._id !== userToDelete._id));
      setGroupMembers(groupMembers.filter(m => m._id !== userToDelete._id));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      setAdminPassword('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteGroupClick = (group) => {
    setGroupToDelete(group);
    setError('');
    setShowDeleteGroupConfirm(true);
  };

  const handleDeleteGroup = async () => {
    setError('');
    try {
      await api.deleteGroup(groupToDelete._id);
      const updatedGroups = groups.filter(g => g._id !== groupToDelete._id);
      setGroups(updatedGroups);
      if (selectedGroup?._id === groupToDelete._id) {
        setSelectedGroup(updatedGroups.length > 0 ? updatedGroups[0] : null);
      }
      setShowDeleteGroupConfirm(false);
      setGroupToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="page-header">
        <h1>Welcome, {user?.name}</h1>
        <p>View predictions and compete with others</p>
      </div>

      {/* Group Selector */}
      <div className="group-section card">
        <div className="group-header">
          <h2>Your Groups</h2>
          <div className="group-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowJoinGroup(true)}
            >
              Join Group
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateGroup(true)}
            >
              Create Group
            </button>
          </div>
        </div>

        {groups.length === 0 ? (
          <p className="no-groups">
            You're not in any groups yet. Create or join one to start!
          </p>
        ) : (
          <div className="group-selector">
            <label>Select Group:</label>
            <select
              value={selectedGroup?._id || ''}
              onChange={(e) => {
                const group = groups.find(g => g._id === e.target.value);
                setSelectedGroup(group);
              }}
            >
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </select>
            {selectedGroup && (
              <div className="invite-code">
                Invite Code: <strong>{selectedGroup.inviteCode}</strong>
                {user?.isAdmin && (
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteGroupClick(selectedGroup)}
                    title="Delete group"
                    style={{ marginLeft: '10px' }}
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="modal-overlay" onClick={() => setShowCreateGroup(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create Group</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleCreateGroup}>
              <input
                type="text"
                placeholder="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                required
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateGroup(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Join Group Modal */}
      {showJoinGroup && (
        <div className="modal-overlay" onClick={() => setShowJoinGroup(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Join Group</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleJoinGroup}>
              <input
                type="text"
                placeholder="Invite Code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                required
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowJoinGroup(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Join
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete User</h2>
            <p className="delete-warning">
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
              This will remove all their predictions and group memberships.
            </p>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleDeleteUser}>
              <input
                type="password"
                placeholder="Enter admin password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
              />
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-danger">
                  Delete
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteGroupConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Group</h2>
            <p className="delete-warning">
              Are you sure you want to delete <strong>{groupToDelete?.name}</strong>?
              This will remove all predictions associated with this group.
            </p>
            {error && <div className="error-message">{error}</div>}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteGroupConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteGroup}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members Tabs */}
      {selectedGroup && (
        <section className="members-section">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'group' ? 'active' : ''}`}
              onClick={() => setActiveTab('group')}
            >
              {selectedGroup.name} Members
            </button>
            <button
              className={`tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Members
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'group' && (
              <div className="members-list">
                {groupMembers.map(member => (
                  <div
                    key={member._id}
                    className="list-item"
                    onClick={() => handleUserClick(member._id)}
                  >
                    <div className="list-item-avatar">
                      {member.profilePic ? (
                        <img src={member.profilePic} alt={member.name} />
                      ) : (
                        member.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-name">
                        {member.name}
                        {member._id === user?._id && <span className="me-badge">Me</span>}
                      </div>
                      <div className="list-item-meta">
                        {member.predictionsCount}/22 predictions
                        {member.score > 0 && ` | Score: ${member.score}`}
                      </div>
                    </div>
                    <div className="list-item-badge">
                      {Math.round((member.predictionsCount / 22) * 100)}%
                    </div>
                  </div>
                ))}
                {groupMembers.length === 0 && (
                  <p className="no-members">No members in this group yet.</p>
                )}
              </div>
            )}

            {activeTab === 'all' && (
              <div className="members-list">
                {allUsers.map(member => (
                  <div
                    key={member._id}
                    className="list-item"
                    onClick={() => handleUserClick(member._id)}
                  >
                    <div className="list-item-avatar">
                      {member.profilePic ? (
                        <img src={member.profilePic} alt={member.name} />
                      ) : (
                        member.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-name">
                        {member.name}
                        {member._id === user?._id && <span className="me-badge">Me</span>}
                      </div>
                    </div>
                    {user?.isAdmin && member._id !== user?._id && !member.isAdmin && (
                      <button
                        className="btn-delete"
                        onClick={(e) => handleDeleteClick(e, member)}
                        title="Delete user"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {allUsers.length === 0 && (
                  <p className="no-members">No users found.</p>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
